import { useRuntimeConfig, useNuxtApp } from "#imports";
import { useSiteSettingsStore } from "@/store/index";

const DEFAULT_ORGANIZATION_NAME = "";
const DEFAULT_LOGO_PATH = "";
const DEFAULT_SAME_AS: string[] = [];
const ABSOLUTE_URL_REGEX = /^https?:\/\//i;

type SchemaDate = string | number | Date | null | undefined;

type OrganizationOptions = {
  name?: string;
  url?: string;
  sameAs?: string[];
  logoUrl?: string;
};

export type WebPageSchemaOptions = {
  type?: string;
  url: string;
  id?: string;
  name?: string;
  headline?: string;
  description?: string;
  publishedAt?: SchemaDate;
  updatedAt?: SchemaDate;
  keywords?: string | string[];
  authorName?: string;
  authorUrl?: string;
  publisherName?: string;
  publisherUrl?: string;
  baseUrl?: string;
  logoUrl?: string;
  logoPath?: string;
  sameAs?: string[];
  mainEntityType?: string;
  mainEntityId?: string;
  includeMainEntity?: boolean;
  image?:
  | string
  | {
    url?: string;
    width?: number;
    height?: number;
  };
  offers?: any;
  brand?: any;
  breadcrumbs?: Array<{ title: string; link?: string }>;
};

export type LocalBusinessSchemaOptions = {
  name: string;
  url: string;
  logoUrl?: string;
  image?: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  geo?: {
    latitude?: number;
    longitude?: number;
  };
  telephone?: string;
  priceRange?: string;
  openingHours?: string | string[];
  sameAs?: string[];
};

export type BlogPostingSchemaOptions = {
  url: string;
  title?: string;
  description?: string;
  publishedAt?: SchemaDate;
  updatedAt?: SchemaDate;
  keywords?: string | string[];
  authorName?: string;
  authorUrl?: string;
  publisherName?: string;
  publisherUrl?: string;
  baseUrl?: string;
  logoUrl?: string;
  logoPath?: string;
  sameAs?: string[];
  image?:
  | string
  | {
    url?: string;
    width?: number;
    height?: number;
  };
  articleBody?: string;
  wordCount?: number;
  mainEntityId?: string;
  isPartOf?: Record<string, any>;
};

export type JsonLdEntry = {
  schema: Record<string, any> | null | undefined;
  key?: string;
};

export type FaqItem = {
  title?: string;
  description?: string;
  enabled?: boolean;
};

const ISO_DATE_LENGTH = 24;

function formatSchemaDate(value: SchemaDate) {
  if (value === null || value === undefined) {
    return undefined;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  const iso = date.toISOString();
  return iso.length === ISO_DATE_LENGTH ? iso : undefined;
}

export function resolveMediaUrl(path?: string | null, baseUrl?: string) {
  if (!path) {
    return undefined;
  }
  if (ABSOLUTE_URL_REGEX.test(path)) {
    return path;
  }
  if (baseUrl) {
    return `${baseUrl}${path}`;
  }
  return path;
}

function extractSocialLinks(values?: any) {
  if (!values) {
    return [];
  }

  const entries = Array.isArray(values) ? values : [values];

  return entries
    .map((entry) => {
      if (!entry) return null;
      if (typeof entry === "string") {
        return entry.trim();
      }
      if (typeof entry === "object") {
        if (entry.enabled === false) {
          return null;
        }
        const candidate =
          entry.link ||
          entry.url ||
          entry.href ||
          entry.value ||
          entry.text ||
          entry?.attributes?.link ||
          entry?.attributes?.url ||
          entry?.attributes?.href;
        if (typeof candidate === "string") {
          return candidate.trim();
        }
      }
      return null;
    })
    .filter(
      (url): url is string => typeof url === "string" && ABSOLUTE_URL_REGEX.test(url)
    );
}

function getSchemaOrganizationDefaults() {
  let piniaInstance = null;
  try {
    const nuxtApp = useNuxtApp();
    piniaInstance = nuxtApp?.$pinia || null;
  } catch {
    piniaInstance = null;
  }

  const siteSettings = piniaInstance
    ? useSiteSettingsStore(piniaInstance)
    : null;

  let runtimeConfig;
  try {
    runtimeConfig = useRuntimeConfig();
  } catch {
    runtimeConfig = { public: { baseUrl: "" } };
  }
  const baseUrl = runtimeConfig?.public?.baseUrl || "";

  const organizationName =
    siteSettings?.brandName?.trim() || DEFAULT_ORGANIZATION_NAME;
  const logoPath = siteSettings?.logo || DEFAULT_LOGO_PATH;
  const socialLinks = extractSocialLinks(siteSettings?.socialMedia);

  return {
    name: organizationName,
    logoUrl:
      resolveMediaUrl(logoPath, baseUrl) ||
      resolveMediaUrl(DEFAULT_LOGO_PATH, baseUrl) ||
      DEFAULT_LOGO_PATH,
    sameAs: socialLinks.length ? socialLinks : DEFAULT_SAME_AS,
  };
}

function buildOrganization({
  name,
  url,
  sameAs,
  logoUrl,
}: OrganizationOptions = {}) {
  const organization: Record<string, any> = {
    "@type": "Organization",
    name: name || DEFAULT_ORGANIZATION_NAME,
  };

  if (url) {
    organization.url = url;
  }

  if (logoUrl) {
    organization.logo = {
      "@type": "ImageObject",
      url: logoUrl,
    };
  }

  if (sameAs?.length) {
    organization.sameAs = sameAs;
  }

  return organization;
}

export function stripHtmlTags(value?: string | null) {
  if (!value) {
    return "";
  }
  return value.replace(/<\/?[^>]+(>|$)/g, " ").replace(/\s+/g, " ").trim();
}

export function createFaqSchema(items?: FaqItem[] | null) {
  if (!items?.length) {
    return null;
  }

  const mainEntity = items
    .map((item) => ({
      title: item?.title?.trim() || "",
      description: stripHtmlTags(item?.description),
      enabled: item?.enabled ?? true,
    }))
    .filter((item) => item.enabled && item.title && item.description)
    .map((item) => ({
      "@type": "Question",
      name: item.title,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.description,
      },
    }));

  if (!mainEntity.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

export function createBreadcrumbSchema(
  items: Array<{ title: string; link?: string }>,
  baseUrl?: string,
) {
  if (!items?.length) {
    return null;
  }

  const itemListElement = items.map((item, index) => {
    const position = index + 1;
    let name = stripHtmlTags(item.title);

    // First item with SVG icon: Google requires a non-empty name
    if (!name && index === 0) {
      name = "Ana Sayfa";
    }

    const itemUrl = item.link ? resolveMediaUrl(item.link, baseUrl) : undefined;

    const element: Record<string, any> = {
      "@type": "ListItem",
      position,
      name,
    };

    if (itemUrl) {
      element.item = itemUrl;
    }

    return element;
  });

  // Google requires every ListItem to have a "name".
  // Filter out items that ended up without a name.
  const validItems = itemListElement.filter(
    (el) => el.name && el.name.trim() !== ""
  );

  if (!validItems.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: validItems,
  };
}

export function createLocalBusinessSchema(options: LocalBusinessSchemaOptions) {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: options.name,
    url: options.url,
  };

  if (options.logoUrl) schema.logo = options.logoUrl;
  if (options.image) schema.image = options.image;
  if (options.telephone) schema.telephone = options.telephone;
  if (options.priceRange) schema.priceRange = options.priceRange;
  if (options.openingHours) schema.openingHours = options.openingHours;
  if (options.sameAs?.length) schema.sameAs = options.sameAs;

  if (options.address) {
    schema.address = {
      "@type": "PostalAddress",
      ...options.address,
    };
  }

  if (options.geo) {
    schema.geo = {
      "@type": "GeoCoordinates",
      ...options.geo,
    };
  }

  return schema;
}

function resolveLogoUrl(
  logoUrl?: string,
  logoPath?: string,
  baseUrl?: string,
  fallbackLogo?: string
) {
  return (
    resolveMediaUrl(logoUrl, baseUrl) ||
    resolveMediaUrl(logoPath, baseUrl) ||
    fallbackLogo ||
    resolveMediaUrl(DEFAULT_LOGO_PATH, baseUrl) ||
    DEFAULT_LOGO_PATH
  );
}

export function createWebPageSchema(options: WebPageSchemaOptions) {
  if (!options.url) {
    return null;
  }

  const organizationDefaults = getSchemaOrganizationDefaults();
  const sameAs = options.sameAs?.length
    ? options.sameAs
    : organizationDefaults.sameAs;

  const logoUrl = resolveLogoUrl(
    options.logoUrl,
    options.logoPath,
    options.baseUrl,
    organizationDefaults.logoUrl
  );

  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": options.type || "WebPage",
    "@id": options.id || options.url,
    url: options.url,
    name: options.name,
    headline: options.headline || options.name,
    description: options.description,
    datePublished: formatSchemaDate(options.publishedAt),
    dateModified: formatSchemaDate(options.updatedAt),
    author: buildOrganization({
      name: options.authorName || organizationDefaults.name,
      url: options.authorUrl || options.baseUrl || options.url,
    }),
    publisher: buildOrganization({
      name: options.publisherName || organizationDefaults.name,
      url: options.publisherUrl || options.url,
      logoUrl,
      sameAs,
    }),
  };

  if (options.keywords) {
    schema.keywords = options.keywords;
  }

  if (options.image) {
    if (typeof options.image === "string") {
      schema.image = options.image;
    } else if (options.image.url) {
      schema.image = {
        "@type": "ImageObject",
        url: options.image.url,
        width: options.image.width,
        height: options.image.height,
      };
    }
  }

  if (options.includeMainEntity !== false) {
    schema.mainEntityOfPage = {
      "@type": options.mainEntityType || schema["@type"],
      "@id": options.mainEntityId || options.url,
    };
  }

  return schema;
}

export function createBlogPostingSchema(
  options: BlogPostingSchemaOptions
) {
  if (!options.url) {
    return null;
  }

  const organizationDefaults = getSchemaOrganizationDefaults();
  const sameAs = options.sameAs?.length
    ? options.sameAs
    : organizationDefaults.sameAs;
  const logoUrl = resolveLogoUrl(
    options.logoUrl,
    options.logoPath,
    options.baseUrl,
    organizationDefaults.logoUrl
  );

  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: options.title,
    name: options.title,
    url: options.url,
    description: options.description,
    datePublished: formatSchemaDate(options.publishedAt),
    dateModified: formatSchemaDate(options.updatedAt),
    author: buildOrganization({
      name: options.authorName || organizationDefaults.name,
      url: options.authorUrl || options.baseUrl || options.url,
    }),
    publisher: buildOrganization({
      name: options.publisherName || options.authorName || organizationDefaults.name,
      url: options.publisherUrl || options.url,
      sameAs,
      logoUrl,
    }),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": options.mainEntityId || options.url,
    },
  };

  if (options.keywords) {
    schema.keywords = options.keywords;
  }

  if (options.articleBody) {
    schema.articleBody = stripHtmlTags(options.articleBody);
  }

  if (typeof options.wordCount === "number") {
    schema.wordCount = options.wordCount;
  }

  if (options.isPartOf) {
    schema.isPartOf = options.isPartOf;
  }

  if (options.image) {
    if (typeof options.image === "string") {
      schema.image = options.image;
    } else if (options.image.url) {
      schema.image = {
        "@type": "ImageObject",
        url: resolveMediaUrl(options.image.url, options.baseUrl),
        width: options.image.width,
        height: options.image.height,
      };
    }
  }

  return schema;
}

export function buildJsonLdScripts(
  entries: JsonLdEntry[],
  { keyPrefix = "jsonld" }: { keyPrefix?: string } = {}
) {
  return entries
    .map((entry, index) => {
      if (!entry?.schema) {
        return null;
      }
      return {
        key: entry.key || `${keyPrefix}-${index + 1}`,
        type: "application/ld+json",
        innerHTML: JSON.stringify(entry.schema),
      };
    })
    .filter(Boolean);
}
